import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, XAxis, YAxis } from "recharts";

import { getBillingLocationIncreaseComparison } from "../../../selectors/billing";
import { formatNumber } from "../../../utils/number";

import styles from "./BillingCalculationFeeChangeChart.module.scss";

export const BillingCalculationFeeChangeChart = () => {
  const { t } = useTranslation();
  const locationIncreaseComparison = useSelector(getBillingLocationIncreaseComparison);

  const data = useMemo(
    () => [
      {
        name: t("billingCalculationFeeChangeChart.percentChangeRanges.negative25"),
        count: locationIncreaseComparison.negative25,
        color: "var(--green)",
      },
      {
        name: t("billingCalculationFeeChangeChart.percentChangeRanges.negative20To25"),
        count: locationIncreaseComparison.negative20To25,
        color: "var(--green)",
      },
      {
        name: t("billingCalculationFeeChangeChart.percentChangeRanges.negative15To20"),
        count: locationIncreaseComparison.negative15To20,
        color: "var(--green)",
      },
      {
        name: t("billingCalculationFeeChangeChart.percentChangeRanges.negative10To15"),
        count: locationIncreaseComparison.negative10To15,
        color: "var(--green)",
      },
      {
        name: t("billingCalculationFeeChangeChart.percentChangeRanges.negative5To10"),
        count: locationIncreaseComparison.negative5To10,
        color: "var(--green)",
      },
      {
        name: t("billingCalculationFeeChangeChart.percentChangeRanges.negative0To5"),
        count: locationIncreaseComparison.negative0To5,
        color: "var(--green)",
      },
      {
        name: t("billingCalculationFeeChangeChart.percentChangeRanges.positive0To5"),
        count: locationIncreaseComparison.positive0To5,
        color: "var(--red-600)",
      },
      {
        name: t("billingCalculationFeeChangeChart.percentChangeRanges.positive5To10"),
        count: locationIncreaseComparison.positive5To10,
        color: "var(--red-600)",
      },
      {
        name: t("billingCalculationFeeChangeChart.percentChangeRanges.positive10To15"),
        count: locationIncreaseComparison.positive10To15,
        color: "var(--red-600)",
      },
      {
        name: t("billingCalculationFeeChangeChart.percentChangeRanges.positive15To20"),
        count: locationIncreaseComparison.positive15To20,
        color: "var(--red-600)",
      },
      {
        name: t("billingCalculationFeeChangeChart.percentChangeRanges.positive20To25"),
        count: locationIncreaseComparison.positive20To25,
        color: "var(--red-600)",
      },
      {
        name: t("billingCalculationFeeChangeChart.percentChangeRanges.positive25"),
        count: locationIncreaseComparison.positive25,
        color: "var(--red-600)",
      },
    ],
    [locationIncreaseComparison]
  );

  const yAxisFormatter = (value: number) => {
    if (value === 0) {
      return t("billingCalculationFeeChangeChart.zeroResidents");
    }

    return formatNumber(value);
  };

  return (
    <div className={styles.container}>
      <p className={styles.label}>{t("billingCalculationFeeChangeChart.label")}</p>
      <div style={{ width: "100%", height: "240px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} barCategoryGap="14%">
            <CartesianGrid vertical={false} stroke="var(--gray-200)" />
            <XAxis
              dataKey="name"
              angle={-50}
              textAnchor="end"
              height={75}
              axisLine={false}
              tickLine={false}
              tickMargin={10}
              tick={{ fill: "var(--gray-600)", fontSize: 12 }}
              padding={{ left: 16, right: 16 }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickMargin={10}
              tick={{ fill: "var(--gray-600)", fontSize: 12 }}
              tickFormatter={yAxisFormatter}
            />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
